/* global document */
/* global window */

import { Controller } from '@hotwired/stimulus';
import { useClickOutside } from "stimulus-use";

// https://github.com/github/combobox-nav
import Combobox from "@github/combobox-nav";

// How long to wait after the last keystroke before asking Google for
// suggestions. Keeps us from issuing (billable) requests for every character.
const SUGGESTION_DEBOUNCE_MS = 250;

// Targets
//
// * suggestionItem: A template tag with the HTML for each item in the combobox.
//
// * input: The input field that will be used for the autocomplete.
//
// * postalCode: The value of this element will be set to the postal code of the
//   place selected in the autocomplete dropdown.
//
// * latitude: The value of this element will be set to the latitude of the
//   place selected in the autocomplete dropdown.
//
// * longitude: The value of this element will be set to the longitude of
//   the place selected in the autocomplete dropdown.
//
// * list: The element that will contain the list of suggestions.
//
// Values
//
// * googleKey: The Google Maps API key to use for the autocomplete. This is
//   passed to Google Maps. It must have the following APIs enabled: "Geocoding
//   API", "Maps JavaScript API", "Places API (New)".
//
// * language: The language to use for the autocomplete. This is passed to
//   Google Maps. See
//   https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#AutocompleteRequest.language
//   for details.
//
// * region: Region code to use for formatting, ranking and maybe results
//   filtering. This is passed to Google Maps. See
//   https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#AutocompleteRequest.region
//   for details.
//
// * countryCode: The country code to use for filtering results. This is passed
//   to Google Maps. See
//   https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#AutocompleteRequest.includedRegionCodes
//   for details.
//
// * types: An array of place types to use for filtering results. This is passed
//   to Google Maps. See
//   https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#AutocompleteRequest.includedPrimaryTypes
//   for details.
//
export default class extends Controller {
  static classes = [
    "hidden",
    "show"
  ]

  static targets = [
    "input",
    "latitude",
    "list",
    "longitude",
    "postalCode",
    "suggestionItem"
  ]

  static values = {
    countryCode: String,
    googleKey: String,
    language: String,
    region: String,
    types: Array
  }

  // Adds the given suggestions to the listTarget based on the template in
  // suggestionItemTarget
  addToListTarget(suggestions) {
    suggestions.forEach((suggestion) => {
      // https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#PlacePrediction
      const placePrediction = suggestion.placePrediction;

      // Kept so the selected suggestion can be turned into a Place with
      // toPlace(), which is what carries the session token into the Place
      // Details request. Building a Place from the id alone loses the session.
      this.placePredictions.set(placePrediction.placeId, placePrediction);

      let content = this.suggestionItemTarget.innerHTML
      content = content.replace(/DISPLAY_NAME/g, placePrediction.text)
      content = content.replace(/MAIN_TEXT/g, placePrediction.mainText)
      content = content.replace(/PLACE_ID/g, placePrediction.placeId)
      content = content.replace(/SECONDARY_TEXT/g, placePrediction.secondaryText || "")
      this.listTarget.insertAdjacentHTML('beforeend', content);
    });
  }

  clearListTarget() {
    this.listTarget.innerHTML = "";
    this.placePredictions.clear();
  }

  clearPostalCodeTarget() {
    if (!this.hasPostalCodeTarget) { return }

    this.postalCodeTarget.value = "";
  }

  clearSession() {
    this.sessionToken = null;
  }

  clickOutside(event) {
    if (this.listTarget.classList.contains(this.hiddenClass)) return;

    // Deliberately does not commit a suggestion. The rendered list can lag
    // behind what has been typed, so committing from here used to store a place
    // for an earlier, shorter prefix. Anything the user did not explicitly pick
    // is resolved on submit instead, see handleSubmit.
    this.hide();
  }

  connect () {
    useClickOutside(this);

    this.suggestionGeneration = 0;
    this.placePredictions = new Map();
    this.createCombobox();
    this.setup();
    this.disableNativeAutocomplete();
    this.trackPrefilledInput();
  }

  createCombobox() {
    this.combobox = new Combobox(
      this.inputTarget,
      this.listTarget,
      {
        firstOptionSelectionMode: "active"
      }
    )

    this.listTarget.addEventListener('combobox-commit', this.elementSelected.bind(this))
  }

  disableNativeAutocomplete() {
    this.inputTarget.setAttribute("autocomplete", "off")
  }

  disconnect() {
    if (this.combobox) { this.combobox.destroy() };
    if (this.debounceTimeout) { clearTimeout(this.debounceTimeout) };
    if (this.form && this.submitHandler) {
      this.form.removeEventListener("submit", this.submitHandler);
    }
    if (this.preloadHandler) {
      this.inputTarget.removeEventListener("focus", this.preloadHandler);
      this.inputTarget.removeEventListener("input", this.preloadHandler);
      this.inputTarget.removeEventListener("mouseover", this.preloadHandler);
    }
  }

  elementSelected(event) {
    this.handleElementSelected(event.target);
    this.hide();
  }

  // Create and returns a new session token if one does not exist for the
  // current session.
  ensureSession() {
    if (!this.sessionToken) {
      return this.startSession()
    }

    return this.sessionToken;
  }

  // Returns the postal code for a Place. If the Place has address components
  // with a postal code that is returned. If not, we use reverse geocoding to
  // get the postal code for the location of the Place.
  async fetchPostalCodeFromPlace(place) {
    if (!place) { return }

    let postalCode = this.findPostalCode(place.addressComponents)
    if (postalCode) { return postalCode }

    const addressComponents = await this.getAddressComponentsFromLocation(place)
    postalCode = this.findPostalCode(addressComponents)
    return postalCode
  }

  // Debounced entry point for the dropdown. Correctness at submit time does not
  // depend on this having finished, see resolveFromInput.
  fetchSuggestions(event) {
    if (this.debounceTimeout) { clearTimeout(this.debounceTimeout) }

    this.debounceTimeout = setTimeout(() => {
      this.debounceTimeout = null;
      this.updateSuggestions().catch(() => this.hide());
    }, SUGGESTION_DEBOUNCE_MS);
  }

  // Returns the postal code from addressComponents.
  findPostalCode(addressComponents) {
    return addressComponents.find(component =>
      component.types.includes("postal_code") &&
      !component.types.includes("postal_code_prefix")
    );
  }

  async getAddressComponentsFromLocation(place) {
    // TODO: Should we preload this? Ensure it's only loaded once?
    const { Geocoder } = await google.maps.importLibrary("geocoding");
    const geocoder = new Geocoder();
    const response = await geocoder.geocode({location: place.location});

    // Get the postal code from the result
    const firstResult = response.results[0];
    if (firstResult) {
      return firstResult.address_components;
    } else {
      return [];
    }
  }

  // Receives a list element from the combobox and updates the UI accordingly.
  async handleElementSelected(listElement, updateInput = true) {
    if (updateInput) {
      const displayName = listElement.dataset.displayName;
      this.inputTarget.value = displayName;
    }

    // Nothing below is allowed to reject. This runs fire-and-forget from the
    // combobox commit handler, so a rejection would surface as an unhandled
    // rejection and leave the hidden fields holding whatever was there before.
    try {
      // toPlace() keeps the Place Details request inside the autocomplete
      // session, which is what makes the suggestion requests billable as one
      // session rather than individually. Falling back to a Place built from
      // the id only costs us that grouping, so it is worth doing but not worth
      // failing over.
      // https://developers.google.com/maps/documentation/javascript/reference/place
      const placePrediction = this.placePredictions.get(listElement.id);
      const place = placePrediction ? placePrediction.toPlace() : new this.Place({
        id: listElement.id,
        requestedLanguage: this.languageValue,
        requestedRegion: this.regionValue
      });

      // https://developers.google.com/maps/documentation/javascript/reference/place#Place.fetchFields
      // https://developers.google.com/maps/documentation/javascript/reference/place#FetchFieldsRequest
      await place.fetchFields({
        fields: [
          "addressComponents",
          "location"
        ]
      })
      this.clearSession()

      if (place.addressComponents) {
        await this.updateTargets(place);

        // Remember which text the hidden fields describe, so we can tell on
        // submit whether they still match what the user typed.
        this.resolvedFor = this.inputTarget.value;
      }
    } catch (error) {
      // Google can fail for reasons we can do nothing about - network, quota,
      // a place id that no longer resolves. Keeping half-resolved or stale
      // coordinates would search the wrong location, so drop them and let the
      // search run without one. The typed text still submits as `place`.
      this.clearSession();
      this.resetFormFields();
      this.resolvedFor = null;
    }
  }

  // The hidden postal code and coordinate fields are filled in asynchronously
  // from Google, which takes several round trips. Submitting is synchronous, so
  // without this the form would be sent with whatever happened to be resolved
  // at that moment - for un-picked input that meant a place for an earlier,
  // shorter prefix of what was typed.
  async handleSubmit(event) {
    if (this.submittingAfterResolve) { return }
    if (!this.needsResolution()) { return }

    event.preventDefault();
    this.submittingAfterResolve = true;

    try {
      await this.resolveFromInput();
    } catch (error) {
      // Better to search without a location than with the wrong one. The
      // hidden fields were cleared when the text changed, so this submits the
      // typed text and no coordinates.
    } finally {
      this.hide();
      this.form.requestSubmit();
    }
  }

  // Returns true if inputTarget has a value.
  hasInput() {
    return (
      this.hasInputTarget && this.inputTarget.value && this.inputTarget.value.trim().length > 0
    );
  }

  hide() {
    // With inspiration from https://hybrd.co/posts/stimulus-autocomplete-with-combobox-navigation
    this.listTarget.classList.add(this.hiddenClass);
    this.listTarget.classList.remove(this.showClass);
    this.combobox.clearSelection();
    this.combobox.stop();
  }

  // The typed text no longer describes whatever place the hidden fields hold,
  // so drop them. Submitting with no location beats submitting the wrong one.
  inputChanged() {
    this.resolvedFor = null;
    this.resetFormFields();
    this.fetchSuggestions();
  }

  async loadAutoComplete() {
    if (this.googleMapsIsLoaded) { return }

    const googlekey = this.googleKeyValue;

    // Google Maps Javascript API has it's own guarantee that it loads only
    // once.
    (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=`https://maps.${c}apis.com/maps/api/js?`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
      key: googlekey,
      v: "weekly",
    });

    const { AutocompleteSessionToken, AutocompleteSuggestion, Place} = await google.maps.importLibrary("places");
    this.googleMapsIsLoaded = true;
    this.AutocompleteSessionToken = AutocompleteSessionToken;
    this.AutocompleteSuggestion = AutocompleteSuggestion;
    this.Place = Place;
  }

  // True when the input holds text we have not resolved to a place yet.
  needsResolution() {
    return this.hasInput() && this.resolvedFor !== this.inputTarget.value;
  }

  resetFormFields() {
    if (this.hasLatitudeTarget) { this.latitudeTarget.value = ""; }
    if (this.hasLongitudeTarget) { this.longitudeTarget.value = ""; }
    this.clearPostalCodeTarget();
  }

  // Resolves whatever is currently in the input to a place, by fetching a fresh
  // set of suggestions for the current text and committing the first one. Used
  // when the user typed something but never picked a suggestion.
  async resolveFromInput() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    if (!this.hasInput()) {
      this.resetFormFields();
      return;
    }

    await this.updateSuggestions();
    await this.selectFirstElement(false);
  }

  // Selects the first element in the list, whatever it is, but only if
  // something has been entered into the input.
  async selectFirstElement(updateInput = true) {
    const firstElement = this.combobox.list.children[0];

    if (this.hasInput() && firstElement) {
      await this.handleElementSelected(firstElement, updateInput);
    }
  }

  setup () {
    // Load the Google Maps API when we need it. Caught rather than bound
    // directly, because an async listener that rejects - which this one does
    // whenever the Google script can't be fetched - is an unhandled rejection
    // on every focus and keystroke. Suggestions just stay unavailable, and
    // updateSuggestions awaits the same call when it actually needs it.
    this.preloadHandler = () => { this.loadAutoComplete().catch(() => {}) };
    this.inputTarget.addEventListener("focus", this.preloadHandler);
    this.inputTarget.addEventListener("input", this.preloadHandler);
    this.inputTarget.addEventListener("mouseover", this.preloadHandler);

    this.form = this.element.closest("form");
    if (this.form) {
      this.submitHandler = this.handleSubmit.bind(this);
      this.form.addEventListener("submit", this.submitHandler);
    }
  }

  show() {
    // With inspiration from https://hybrd.co/posts/stimulus-autocomplete-with-combobox-navigation
    this.listTarget.classList.remove(this.hiddenClass);
    this.listTarget.classList.add(this.showClass);
    this.combobox.start();
  }

  // Returns the new token as well as storing it, so the request that triggers
  // the start of a session is itself grouped under that session.
  startSession() {
    if (!this.googleMapsIsLoaded) { return }

    this.sessionToken = new this.AutocompleteSessionToken();

    return this.sessionToken;
  }

  // On the search results page the field is rendered with the current search's
  // place. Treat that as already resolved so submitting an untouched form does
  // not re-resolve (and possibly change) it.
  trackPrefilledInput() {
    if (this.hasInput()) { this.resolvedFor = this.inputTarget.value }
  }

  updateLatitudeLongitudeTargets(place) {
    if (this.hasLatitudeTarget) { this.latitudeTarget.value = place.location.lat(); }
    if (this.hasLongitudeTarget) { this.longitudeTarget.value = place.location.lng(); }
  }

  updatePostalCodeTarget(postalCodeFromAddressComponents) {
    if (!this.hasPostalCodeTarget ) { return }

    if (postalCodeFromAddressComponents) {
      const postalCodeText = postalCodeFromAddressComponents.shortText || postalCodeFromAddressComponents.short_name;
      this.postalCodeTarget.value = postalCodeText;
    }
    else {
      this.clearPostalCodeTarget()
    }
  }

  // Fetches suggestions for the text currently in the input and renders them.
  async updateSuggestions() {
    await this.loadAutoComplete();
    if (!this.googleMapsIsLoaded) { return; }

    if (!this.inputTarget.value) {
      this.clearListTarget();
      this.hide();
      return;
    }

    // https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#AutocompleteRequest
    let request = {
      input: this.inputTarget.value,
      language: this.languageValue,
      region: this.regionValue,
      sessionToken: this.ensureSession(),
      includedRegionCodes: [this.countryCodeValue],
      includedPrimaryTypes: this.typesValue
    };

    const generation = ++this.suggestionGeneration;
    const { suggestions } = await this.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

    // A newer request was issued while this one was in flight. Rendering these
    // results would leave the list describing an older input value.
    if (generation !== this.suggestionGeneration) { return }

    this.clearListTarget();

    if (suggestions && suggestions.length > 0) {
      this.addToListTarget(suggestions);
      this.show()
    } else {
      this.hide()
    }
  }

  async updateTargets (place) {
    let postalCode = await this.fetchPostalCodeFromPlace(place)
    this.updatePostalCodeTarget(postalCode);
    this.updateLatitudeLongitudeTargets(place);
  }
}
